import * as React from 'react';
import { useState, useEffect } from 'react';
import useApiRequest from '../../../api/useApiRequest';
import { styled, Dialog, Box, Typography, DialogTitle, IconButton, Paper } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Slide from '@mui/material/Slide';
import { TransitionProps } from '@mui/material/transitions';
import StatusAndReasonSection from '../utils/StatusSection';
import ActionButtonsFooter from '../utils/ActionButtonsFooter';
import DialogContentSection from '../utils/DialogContentSection';
import UserDetailsTable from '../utils/UserDetailsTable';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../../Redux/store';
import { fetchFiles, fetchFileData, setCurrentFileIndex } from '../../../Redux/fileSlice';
import ProjectManagementFileTabs from '../utils/ProjectManagementFileTabs';

// Styled Dialog
const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogContent-root': {
    padding: theme.spacing(2),
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1),
  },
}));

// Slide Transition
const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Props Interface
interface ProjectManagementAdminFileViewProps {
  open: boolean;
  onClose: () => void;
  name: string;
  id: string;
  functionl1?: string;
  current_project?: string;
  date: string;
  updatedBy?: string;
  status: string;
  reason_to_approve: string;
  reason_to_revert: string;
  sectionTitle: string;
  apiPath: string;
  statusApiPath: string;
  getFileImagePath?: (doc_path: string) => string;
  onSuccess?: () => void;
}

// Main Component
export default function ProjectManagementAdminFileView({
  open,
  onClose,
  name,
  id,
  functionl1,
  current_project,
  date,
  updatedBy,
  status,
  reason_to_approve,
  reason_to_revert,
  sectionTitle,
  apiPath,
  statusApiPath,
  getFileImagePath,
  onSuccess

}: ProjectManagementAdminFileViewProps) {
  const dispatch: AppDispatch = useDispatch();
  const { files, currentFileIndex, loading, error } = useSelector((state: RootState) => state.files as RootState['files']);
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [fileSize, setFileSize] = React.useState<any | null>(null);

  const [approveHidden, setApproveHidden] = useState(status === "Approved" || status === "Reverted");
  const [revertHidden, setRevertHidden] = useState(status === "Reverted");
  const [backgroundColor, setBackgroundColor] = useState(
    status === "Approved" ? "#e6ffed" : status === "Reverted" ? "#ffebeb" : "#e6ffed"
  );
  const [isApprove, setIsApprove] = useState(status === "Approved");

  const [tabText, setTabText] = useState({
    reasonText: status === "Approved" ? reason_to_approve || "" : status === "Reverted" ? reason_to_revert || "" : "",
    tempText: ""
  });
  const [tabState, setTabState] = useState({
    showInput: false,
    isEditing: false
  });
  const [isReasonApproved, setIsReasonApproved] = useState(false);
  const [isReasonRevert, setIsReasonReverted] = useState(false);
  const [clickCount, setClickCount] = useState(0);

  const userName = localStorage.getItem('userName') || undefined;

  const { makeRequest } = useApiRequest(apiPath);
  const { makeRequest: makeStatusRequest } = useApiRequest(statusApiPath);


  const isStatusApprovedOrReverted = (status: string) => {
    return status === 'Approved' || status === 'Reverted';
  };


 const [localStatus, setLocalStatus] = useState(status);
  const [actionInProgress, setActionInProgress] = useState<"Approved" | "Reverted" | null>(null);
  const [isActionInitiated, setIsActionInitiated] = useState(false);


  const resetStateForNewAction = (actionType: "Approved" | "Reverted") => {
    setTabState({ showInput: true, isEditing: true });
    setTabText(prev => ({ ...prev, tempText: "" }));
    setActionInProgress(actionType);
    setIsActionInitiated(true);
    
    if (actionType === "Approved") {
      setRevertHidden(true);
    } else {
      setApproveHidden(true);
    }
  };

  const handleAction = async (action: "Approved" | "Reverted", id: string | undefined) => {
    if (!isActionInitiated) {
      resetStateForNewAction(action);
      return;
    }

    if (!tabText.tempText.trim()) {
      return;
    }

    const requestBody = {
      action,
      id,
      reason: tabText.tempText,
    };

    setBackgroundColor(action === "Approved" ? "#e6ffed" : "#ffebeb");
    setLocalStatus(action);

    try {
      await makeStatusRequest("PATCH", requestBody);
      setApproveHidden(action === "Reverted");
      setRevertHidden(action === "Approved");
      setIsApprove(action === "Approved");
      setTabState(prev => ({ ...prev, isEditing: false }));
      setActionInProgress(null);
      setIsActionInitiated(false);

      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error("Error updating status:", error);
      setActionInProgress(null);
      setIsActionInitiated(false);
    }
  };

  const handleApprove = async (id: string | undefined) => {
    if (localStatus === "Approved") return;
    await handleAction("Approved", id);
  };

  const handleRevert = async () => {
    if (localStatus === "Reverted") return;
    await handleAction("Reverted", id);
  };
  const fetchFiles = async () => {
    try {
      const data = await makeRequest("GET");
      if (data.status === "success") {
        setImageSrc(data.data.doc_path);
        setFileSize(data.data.file_size);
      } else {
        throw new Error(data.message || "Failed to fetch image.");
      }
    } catch (err) {
      console.error("Error fetching files:", err);
    }
  };


  const handleTabChange = (index: number) => {
    const selectedFile = files[index];
    if (!selectedFile.fileData) {
      dispatch(fetchFileData({ docPath: selectedFile.doc_path, index }));
    }
    dispatch(setCurrentFileIndex(index));
  };


  useEffect(() => {
    if (open) {
      fetchFiles();
      // Reset state when opening the dialog
      setLocalStatus(status);
      setApproveHidden(status === "Approved" || status === "Reverted");
      setRevertHidden(status === "Reverted");
      setBackgroundColor(
        status === "Approved" ? "#e6ffed" : status === "Reverted" ? "#ffebeb" : "#e6ffed"
      );
      setTabText({
        reasonText: status === "Approved" ? reason_to_approve || "" : status === "Reverted" ? reason_to_revert || "" : "",
        tempText: ""
      });
      setTabState({ showInput: false, isEditing: false });
      setClickCount(0);
    } else {
      setImageSrc(null);
      setFileSize(null);
    }
  }, [open, id, status]);

  const getFileType = (fileName: string) => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    if (extension === 'pdf') {
      return 'pdf';
    } else if (['jpg', 'jpeg', 'png', 'gif'].includes(extension || '')) {
      return 'image';
    }
    return 'unknown';
  };

  return (
    <BootstrapDialog
      onClose={onClose}
      aria-labelledby="customized-dialog-title"
      open={open}
      TransitionComponent={Transition}
      sx={{
        '& .MuiDialog-paper': {
          width: '50%',
          maxWidth: 800,
          position: 'absolute',
          top: "0%",
          right: "0%",
          bottom: "0%",
          height: '100%', margin: 1,
        },
      }}
    >

      <Box>
        <Typography variant="h6" sx={{ m: 0, p: 2, color: '#000' }} gutterBottom>
          {sectionTitle}
        </Typography>
        <DialogTitle sx={{ m: 0, p: 2, color: '#000' }} id="customized-dialog-title">
          {name.split('_').slice(-1).join('_')}
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={(theme) => ({
            position: 'absolute',
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          })}
        >
          <CloseIcon />
        </IconButton>

        <ProjectManagementFileTabs
          open={open}
          imageSrc={imageSrc}
          apiPath={apiPath}
          getFileType={getFileType}
        />

        <UserDetailsTable userName={userName} updatedBy={updatedBy} date={date} functionl1={functionl1} current_project={current_project} />

        <Box
          sx={{
            display: 'flex',
            gap: 2,
            justifyContent: 'space-between',
            p: 2,
          }}
        >
          {files.map((file, index) => (
            <Paper
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                cursor: 'pointer',
                p: 1.5,
                bgcolor: currentFileIndex === index ? 'grey.300' : 'transparent',
                border: currentFileIndex === index ? '1px solid grey.500' : 'none',
                borderRadius: 1.5,
                width: 200,
                transition: 'background-color 0.3s ease',
              }}
              onClick={() => handleTabChange(index)}
            >
              <Box
                component="img"
                src={getFileImagePath ? getFileImagePath(file.doc_path) : ''}
                alt="File"
                sx={{ height: 50, width: 50 }}
              />
              <Box sx={{ display: 'grid', gap: 1 }}>
                <Typography variant="body1" fontWeight={600} sx={{ fontSize: '12px' }}>
                  {file.file_name.split('_').slice(8).join('_')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '12px' }}>
                  {file.file_size || 'Unknown Size'}
                </Typography>
              </Box>
            </Paper>
          ))}
        </Box>


        <StatusAndReasonSection
  status={localStatus}
  localstatus={actionInProgress || localStatus}
  reason_to_approve={reason_to_approve}
  reason_to_revert={reason_to_revert}
  tabState={tabState}
  tabText={tabText}
  backgroundColor={backgroundColor}
  setTabText={setTabText}
  isActionInitiated={isActionInitiated}
/>

<ActionButtonsFooter
  onClose={onClose}
  status={localStatus}
  revertHidden={revertHidden}
  approveHidden={approveHidden}
  handleRevert={handleRevert}
  handleApprove={() => handleApprove(id)}
  id={id}
  isActionInitiated={isActionInitiated}
  hasReasonText={!!tabText.tempText.trim()}
/>

      </Box>
    </BootstrapDialog>
  );
}